import React, { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  getProductReviews,
  selectProductReviews,
  resetProductReviews,
  ProductReviewsState, // Add this import
} from "../slices/product-reviews.slice";
import {
  getProductData,
  selectGetProductData,
} from "../slices/get-product-data.slice";
import { useParams, useNavigate } from "react-router-dom";

type SortOption = "recent" | "high-to-low" | "low-to-high";

// Back Button Component
const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 mb-6"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span className="font-medium">Back to Orders</span>
    </button>
  );
};

interface StarDisplayProps {
  rating: number;
  size?: "sm" | "md" | "lg";
}

const StarDisplay: React.FC<StarDisplayProps> = ({ rating, size = "md" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  return (
    <div className="flex items-center justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating ? "text-yellow-400" : "text-gray-300"
          }`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

interface RatingBreakdownProps {
  breakdown: { [key: string]: number };
  totalReviews: number;
}

const RatingBreakdown: React.FC<RatingBreakdownProps> = ({
  breakdown,
  totalReviews,
}) => {
  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map((rating) => {
        const count = breakdown[rating.toString()] || 0;
        const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;

        return (
          <div key={rating} className="flex items-center text-sm">
            <span className="w-3 text-gray-600">{rating}</span>
            <div className="ml-1">
              <StarDisplay rating={rating} size="sm" />
            </div>
            <div className="flex-1 mx-3">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
            <span className="w-8 text-gray-600 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

interface ReviewCardProps {
  review: {
    id: string;
    rating: number;
    review: string;
    user_id: string;
    order_id: string;
    created_at: string;
    updated_at: string;
  };
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <StarDisplay rating={review.rating} size="sm" />
          <span className="font-medium text-gray-800">{review.rating}/5</span>
        </div>
        <span className="text-sm text-gray-500">
          {formatDate(review.created_at)}
        </span>
      </div>

      {review.review && (
        <p className="text-gray-700 mb-3 leading-relaxed">{review.review}</p>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>User ID: {review.user_id}</span>
        <span>Order: {review.order_id}</span>
      </div>
    </div>
  );
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center space-x-2 mt-6">
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          currentPage === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
        }`}
      >
        Previous
      </button>

      {/* Page Numbers */}
      {getPageNumbers().map((page, index) => (
        <React.Fragment key={index}>
          {page === "..." ? (
            <span className="px-3 py-2 text-gray-500">...</span>
          ) : (
            <button
              onClick={() => onPageChange(page as number)}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                currentPage === page
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              {page}
            </button>
          )}
        </React.Fragment>
      ))}

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          currentPage === totalPages
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
        }`}
      >
        Next
      </button>
    </div>
  );
};

export function ProductReviewsPage() {
  const { productId } = useParams<{ productId: string }>();
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 5;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const productReviewsState = useAppSelector(selectProductReviews);
  const getProductDataState = useAppSelector(selectGetProductData);

  const { status, data, message } = productReviewsState ?? {};

  // Handle back button click
  const handleBackClick = () => {
    navigate(-1);
  };

  // Get product name from product data if available
  const productName = useMemo(() => {
    if (data?.product_name) {
      return data.product_name;
    }

    // Fallback: try to get from product data state if reviews don't have it
    const productData = getProductDataState.data?.product_data;
    if (productData && productId) {
      const foundProduct = productData.find((p) => p.product_id === productId);
      return foundProduct?.product_name || `Product ${productId}`;
    }

    return `Product ${productId}`;
  }, [data?.product_name, getProductDataState.data?.product_data, productId]);

  useEffect(() => {
    // Fix the productId undefined issue
    if (productId) {
      console.log("🔍 Loading reviews for product:", productId);
      dispatch(resetProductReviews());
      dispatch(getProductReviews(productId));

      // Also try to get product data to fetch the product name
      // This assumes your getProductData action can accept a productId
      // You may need to modify this based on your actual API structure
      console.log("📦 Attempting to load product data for name lookup");
      // dispatch(getProductData({ productId: productId }));
    }
  }, [productId, dispatch]);

  useEffect(() => {
    return () => {
      // Cleanup on component unmount
      dispatch(resetProductReviews());
    };
  }, [dispatch]);

  const sortedReviews = useMemo(() => {
    if (!data?.reviews || data.reviews.length === 0) return [];

    const reviews = [...data.reviews];

    switch (sortBy) {
      case "recent":
        return reviews.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case "high-to-low":
        return reviews.sort((a, b) => b.rating - a.rating);
      case "low-to-high":
        return reviews.sort((a, b) => a.rating - b.rating);
      default:
        return reviews;
    }
  }, [data?.reviews, sortBy]);

  // Pagination logic
  const paginatedReviews = useMemo(() => {
    const startIndex = (currentPage - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    return sortedReviews.slice(startIndex, endIndex);
  }, [sortedReviews, currentPage, reviewsPerPage]);

  const totalPages = Math.ceil(sortedReviews.length / reviewsPerPage);

  // Reset to page 1 when sorting changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of reviews section when page changes
    const reviewsSection = document.getElementById("reviews-section");
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Handle case where productId is undefined
  if (!productId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Invalid Product ID
          </h2>
          <p className="text-gray-600 mb-6">
            No product ID was provided in the URL.
          </p>
          <button
            onClick={handleBackClick}
            className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (status === ProductReviewsState.inProgress) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Loading Reviews...
          </h2>
          <p className="text-gray-600">
            Please wait while we fetch the product reviews.
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === ProductReviewsState.fail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Error Loading Reviews
          </h2>
          <p className="text-gray-600 mb-4">
            {message || "Failed to load product reviews."}
          </p>
          <div className="space-y-3">
            <button
              onClick={() =>
                productId && dispatch(getProductReviews(productId))
              }
              className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors duration-200 mr-3"
            >
              Try Again
            </button>
            <button
              onClick={handleBackClick}
              className="bg-gray-600 text-white py-2 px-6 rounded-md hover:bg-gray-700 transition-colors duration-200"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data or no reviews
  if (!data || data.total_reviews === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackButton onClick={handleBackClick} />
          <div className="flex items-center justify-center">
            <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg text-center">
              <div className="text-gray-400 text-6xl mb-4">📝</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                No Reviews Found
              </h2>
              <p className="text-gray-600">
                {productName !== `Product ${productId}`
                  ? `"${productName}" doesn't have any reviews yet.`
                  : "This product doesn't have any reviews yet."}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Product ID: {productId}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackButton onClick={handleBackClick} />

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Product Reviews
          </h1>
          <p className="text-gray-600">
            Product ID: <span className="font-medium">{data.product_id}</span>
          </p>
          <p className="text-lg text-gray-800 font-medium">{productName}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reviews Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Average Rating
              </h2>

              {/* Average Rating */}
              <div className="text-center mb-6 pb-6 border-b border-gray-200">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {data.average_rating}
                </div>
                <div className="flex justify-center mb-2">
                  <StarDisplay
                    rating={Math.round(data.average_rating)}
                    size="lg"
                  />
                </div>
                <p className="text-gray-600 mt-2">
                  Based on {data.total_reviews} review
                  {data.total_reviews !== 1 ? "s" : ""}
                </p>
              </div>

              {/* Rating Breakdown */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">
                  Rating Breakdown
                </h3>
                <RatingBreakdown
                  breakdown={data.rating_breakdown}
                  totalReviews={data.total_reviews}
                />
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-2" id="reviews-section">
            {/* Sort Controls */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Customer Reviews ({data.total_reviews})
                  {totalPages > 1 && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      (Page {currentPage} of {totalPages})
                    </span>
                  )}
                </h2>
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="sort"
                    className="text-sm font-medium text-gray-700"
                  >
                    Sort by:
                  </label>
                  <select
                    id="sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="high-to-low">Highest Rating</option>
                    <option value="low-to-high">Lowest Rating</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Reviews */}
            <div className="space-y-4">
              {paginatedReviews.length > 0 ? (
                <>
                  {paginatedReviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}

                  {/* Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <div className="text-gray-400 text-4xl mb-4">💬</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Reviews Yet
                  </h3>
                  <p className="text-gray-600">
                    Be the first to review this product!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
